import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress } from '@material-ui/core'
import SydModal from '../../commonDesign/SydModal'
import SydButton from '../../commonDesign/Button'
import Alert from '../../atoms/Alert'
import { ALERT_SEVERITY, ALERT_VARIANT } from '../../../constants'
import { useGetStatusReportSummary, useGetStatusReportSummaryConfigs } from '../../../api/groups'
import MarkdownViewer from '../../../apps/aiApp/Assistant/Attachments/viewers/MarkdownViewer'

/**
 * Simplified summary modal for abundance engine that automatically uses the default summary config
 * For the admin version with config selection, see pages/admin/StatusReports/.../SummaryReportModal
 */
function StatusReportSummaryModal ({
  open,
  onClose,
  statusReportId
}) {
  const [summaryConfigId, setSummaryConfigId] = useState(null)

  // Get default summary config
  const { data: summaryConfigs, isFetching: isFetchingSummaryConfigs } = useGetStatusReportSummaryConfigs({
    purposeKey: 'summary'
  }, {
    select: (data) => data?.configs
  })

  // Automatically select the default config
  useEffect(() => {
    const defaultConfig = summaryConfigs?.find(({ isDefault }) => isDefault)
    if (!summaryConfigId && defaultConfig) {
      setSummaryConfigId(defaultConfig?.summaryConfigId)
    }
  }, [summaryConfigs, summaryConfigId])

  const { data, isFetching, isError, refetch } = useGetStatusReportSummary(
    { statusReportId, summaryConfigId },
    { enabled: !!summaryConfigId }
  )

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title='Status Report Summary'
      size='xmedium'
    >
      <div className='flex flex-col'>
        {isError && (
          <div className='flex gap-5 items-center mb-5'>
            <div className='flex-1'>
              <Alert variant={ALERT_VARIANT.standard} severity={ALERT_SEVERITY.error}>
                <div className='flex justify-between items-center w-full'>
                  <div className='flex-1'>An error occurred generating this report</div>
                </div>
              </Alert>
            </div>
            <SydButton variant='primary' size='sm' onClick={() => refetch()}>
              Try Again
            </SydButton>
          </div>
        )}

        <div
          className='flex items-center justify-center my-4 w-full'
        >
          {(isFetching || isFetchingSummaryConfigs) ? (
            <CircularProgress />
          ) : (!isError && data?.markdown) ? (
            <div
              className='border radius-lg w-full'
              style={{
                maxHeight: 'calc(100vh - 400px)',
                overflowY: 'auto'
              }}
            >
              <MarkdownViewer data={data?.markdown || ''} isLoading={isFetching} />
            </div>
          ) : null}
        </div>
      </div>
    </SydModal>
  )
}

StatusReportSummaryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  statusReportId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired
}

export default StatusReportSummaryModal
